import { ChainData, ChainType, CosmosChain } from "@0xsquid/squid-types";
import { SigningCosmWasmClient } from "@cosmjs/cosmwasm-stargate";
import { fromBech32 } from "@cosmjs/encoding";
import { OfflineSigner } from "@cosmjs/proto-signing";
import { SigningStargateClient } from "@cosmjs/stargate";
import { ChainInfo, Keplr, SettledResponses } from "@keplr-wallet/types";
import { CHAIN_IDS } from "../../core/constants";
import { ICosmosContextData } from "../../core/providers/CosmosProvider";
import { ConnectWalletResult, CosmosWallet } from "../../core/types/wallet";
import { isUserRejectionError, normalizeError } from "./errorService";
import { isXionSmartContractAddress } from "./xionService";

type AddressData = { address: string; chainId: string };

/**
 * Get the cosmos address for a given chain
 * This might pop up a keplr modal to approve the chain
 * @param chainId
 * @param keplrTypeWallet
 * @returns Cosmos address
 */
export const getCosmosKey = async (
  chainId?: string,
  keplrTypeWallet?: Keplr
): Promise<string | undefined> => {
  if (!chainId || !keplrTypeWallet) return undefined;
  const key = await keplrTypeWallet?.getKey(chainId);
  return key.bech32Address;
};

export const getKeysSettled = async (
  chainIds: string[],
  keplrTypeWallet: Keplr
): Promise<SettledResponses<string | undefined>> => {
  if (!keplrTypeWallet) return [];

  const paramArray = chainIds.map((chainId) =>
    getCosmosKey(chainId, keplrTypeWallet)
  );

  return Promise.allSettled(paramArray);
};

/**
 * Get the cosmos address for a given chain
 * This might pop up a keplr modal to approve the chain
 * @param chainId
 * @param keplrTypeWallet
 * @returns Cosmos address
 */
export const getAllKeysForSupportedCosmosChains = async (
  chainIds: string[],
  keplrTypeWallet: Keplr | undefined
): Promise<AddressData[]> => {
  if (!keplrTypeWallet) return [];

  const addresses: AddressData[] = [];

  if (typeof keplrTypeWallet.getChainInfosWithoutEndpoints === "function") {
    try {
      const keys = await getKeysSettled(chainIds, keplrTypeWallet);

      keys.forEach((key, index) => {
        if (key.status === "fulfilled" && key.value) {
          addresses.push({
            address: key.value,
            chainId: chainIds[index],
          });
        }
      });

      return addresses;
    } catch (error) {
      console.debug("Batch key fetch failed, trying sequential", error);
    }
  }

  for (const chainId of chainIds) {
    try {
      const keys = await getKeysSettled([chainId], keplrTypeWallet);

      keys.forEach((key) => {
        if (key.status === "fulfilled" && key.value) {
          const bech32Address = key.value;
          addresses.push({
            address: bech32Address,
            chainId,
          });
        }
      });
    } catch (error) {
      console.debug(`Failed to get keys for chain ${chainId}:`, error);
    }
  }

  return addresses;
};

export const isCosmosAddressValid = (chainPrefix: string, address: string) => {
  try {
    if (!address.toLowerCase().startsWith(chainPrefix)) {
      throw new Error("Invalid address for this chain");
    }
    fromBech32(address);
    return true;
  } catch (error) {
    return false;
  }
};

export const getCosmosSigningClient = async ({
  chainRpc,
  cosmosSigner,
  defaultSigningClient,
}: {
  chainRpc: string;
  cosmosSigner: OfflineSigner | null;
  defaultSigningClient:
    | typeof SigningStargateClient
    | typeof SigningCosmWasmClient;
}): Promise<SigningCosmWasmClient | SigningStargateClient | null> => {
  if (!cosmosSigner) return null;
  // const cosmosClient = (await isNomos())
  // ? SigningNomosClient.connectWithSigner(chainRpc, cosmosSigner) // use Nomos provider
  return defaultSigningClient.connectWithSigner(chainRpc, cosmosSigner!); // use default cosmos provider
};

export const getCosmosChainInfosObject = (chain: ChainData): ChainInfo => {
  // Clone the chain to avoid mutating the original object
  const cosmosChain = structuredClone(chain as CosmosChain);
  //
  // TODO: In the future, it will be better to do this parsing on backend side.
  //
  // The `gasPriceStep` field of the `ChainInfo` has been moved under `feeCurrencies`.
  // This is automatically handled as of right now, but the upcoming update would potentially cause errors.
  delete cosmosChain.gasPriceStep;

  // The `coinType` field of the `ChainInfo` is removed. This is automatically handled as of right now,
  // but the upcoming update would potentially cause errors.
  delete cosmosChain.coinType;

  return {
    ...cosmosChain,
    feeCurrencies: cosmosChain.feeCurrencies.map((feeCurrency) => ({
      ...feeCurrency,
      // Gas price step is now under feeCurrencies
      gasPriceStep: cosmosChain.gasPriceStep,
    })),
    chainName: cosmosChain.networkName,
    chainId: cosmosChain.chainId.toString(),
    rpc: cosmosChain.rpc.split("?chain")[0],
  };
};

export const connectCosmosWallet = async (
  wallet: CosmosWallet,
  defaultChain: ChainData | undefined,
  {
    connectCosmos,
    cosmosConnectedWallet,
  }: Pick<ICosmosContextData, "connectCosmos" | "cosmosConnectedWallet">
): Promise<ConnectWalletResult<ChainType.COSMOS> | null> => {
  if (
    wallet.connectorId === cosmosConnectedWallet?.connectorId ||
    defaultChain === undefined
  ) {
    return null;
  }

  const address = await connectCosmos?.mutateAsync({
    chain: defaultChain,
    wallet: wallet,
  });

  if (address) {
    return { wallet, address };
  }

  return null;
};

export const isFallbackAddressNeeded = ({
  currentFallbackAddress,
  address,
  chain,
}: {
  currentFallbackAddress: string | undefined;
  address: string | undefined;
  chain: Pick<ChainData, "chainType" | "chainId"> | undefined;
}): boolean => {
  // If fallback address is already set or chain is not Cosmos
  // fallback address is not needed
  if (currentFallbackAddress || chain?.chainType !== ChainType.COSMOS) {
    return false;
  }

  // For Xion, check if address is 63 chars (smart contract account abstraction)
  if (chain.chainId === CHAIN_IDS.XION) {
    return isXionSmartContractAddress(address);
  }

  // For other Cosmos chains, check coinType
  return (chain as CosmosChain).coinType !== 118;
};

/**
 * Cosmos wallets may throw an error if the chain the user is trying to use is not added to the wallet.
 * For those cases, some wallets expose a method for adding new chains to the user wallet.
 *
 * This method will try to add the provided chain, throwing an error if the request fails
 * or if the original error indicates that the user rejected the request.
 */
export async function suggestChainOrThrow({
  keplrTypeWallet,
  chain,
  error,
}: {
  keplrTypeWallet: Keplr;
  chain: ChainData;
  error: unknown;
}): Promise<void> {
  if (isUserRejectionError(normalizeError(error))) {
    throw error;
  }

  if (typeof keplrTypeWallet.experimentalSuggestChain !== "function") {
    throw error;
  }

  await keplrTypeWallet.experimentalSuggestChain(
    getCosmosChainInfosObject(chain)
  );
}
